import { useNavigate, useParams, useLocation } from "react-router";
import useMutation from "src/hooks/useMutation";
import { CREATE_ASSET_MUTATION, CREATE_SKU_MUTATION } from "src/constants/graphql/index";
import { ADD_SHIPMENT_ITEMS_MUTATION } from "src/constants/graphql/mutations";
import { GET_SHIPMENT_QUERY } from "src/constants/graphql/queries";

export default function useShipmentActions(shipmentId, projectIdProp) {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId: routeProjectId } = useParams();
  const projectId = projectIdProp || routeProjectId;
  const [createAsset, { loading: createAssetLoading }] = useMutation(CREATE_ASSET_MUTATION);
  const [createSku, { loading: createSkuLoading }] = useMutation(CREATE_SKU_MUTATION);
  const [addShipmentItem, { loading: addShipmentItemLoading }] = useMutation(
    ADD_SHIPMENT_ITEMS_MUTATION,
    {
      refetchQueries: [{ query: GET_SHIPMENT_QUERY, variables: { id: shipmentId } }],
    }
  );

  const handleCreateAsset = (input, { skipNotifications = false, skipRefreshCache = false } = {}) => {
    return createAsset({ variables: { projectId, input, skipNotifications, skipRefreshCache } });
  };

  const handleCreateShipment = async (input = {}, { skipNotifications = false, skipRefetch = false, skipRefreshCache = false } = {}) => {
    const data = await handleCreateAsset(input, { skipNotifications, skipRefreshCache });
    const asset = data.data.createAsset.asset;

    const mutationOptions = {
      variables: {
        assetId: asset.id,
        shipmentId,
        quantity: 0,
      },
    };

    // During bulk operations, skip the per-item shipment refetch
    if (skipRefetch) {
      mutationOptions.refetchQueries = [];
    }

    const item = await addShipmentItem(mutationOptions);

    return { ...data, shipmentItem: item.data.addShipmentItems.shipmentItem };
  };

  const createShipment = (input, options = {}) => {
    const { openNewItem = true, duplicate = false, skipNotifications = false, skipRefetch = false, skipRefreshCache = false } = options;

    return handleCreateShipment(input, { skipNotifications, skipRefetch, skipRefreshCache }).then(data => {
      if (openNewItem) {
        const asset = data.data.createAsset.asset;
        const pathname = window.location.pathname;
        const params = new URLSearchParams(location.search);

        params.set("assetId", asset.id);
        params.set("shipmentItemId", data.shipmentItem.id);
        params.set("addShipmentItem", "true");
        params.delete("itemLookup");

        if (duplicate) {
          params.set("duplicate", "true");
          params.set("newItem", "true");
        }

        navigate({ pathname, search: params.toString() });
      }
      return data;
    });
  };

  return {
    createShipment,
    createAsset: handleCreateAsset,
    createSku: (input) => createSku({ variables: { projectId, input } }),
    createAssetLoading: createAssetLoading || createSkuLoading,
    addShipmentItemLoading,
  };
}
